import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileUp, Loader2, Play, Plus, RefreshCw, Trash2, Truck } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { SITE_ID } from '@/config/site';
import { useUIStore } from '@/stores/ui-store';
import StoreMigrationBrand, { type BrandOptions } from './StoreMigrationBrand';

/**
 * Moving a store to Rumio — platform admin only, on the platform's own admin
 * (the migrations and the run live with the platform; see
 * functions/src/routes/admin/store-migrations.ts).
 *
 * Each migration is for one platform (a "connector" on the server: what it
 * needs, which files it reads, which steps it has). The store's export files
 * are uploaded here in pieces, then a run loads them into the chosen site,
 * step by step, in a process of its own on the server. Every step is safe to
 * run again. Nothing a run does sends mail to the store's customers.
 */

/** A step's name, e.g. 'catalog'; each platform has its own list. */
type StepName = string;

/** What the server says a platform needs (GET /connectors, and with each migration). */
interface ConnectorInfo {
  platform: string;
  label: string;
  credentials: { fields: { key: string; label: string; secret: boolean; placeholder?: string }[]; optional: boolean; note?: string } | null;
  uploads: { accept: string[]; hint: string };
  steps: { name: string; detail?: string }[];
}

interface StepState {
  status: 'running' | 'done' | 'partial' | 'skipped' | 'failed';
  startedAt?: string;
  finishedAt?: string;
  report?: Record<string, unknown>;
  warnings?: { count: number; sample: string[] };
  reason?: string;
  error?: string;
}

interface Migration {
  id: string;
  source_platform: string;
  source_url: string;
  target_site_id: string | null;
  status: string;
  options: { timeZone?: string; siteJobId?: string } & BrandOptions;
  progress: {
    run?: { startedAt: string; finishedAt?: string; heartbeatAt: string; steps: StepName[] };
    steps?: Partial<Record<StepName, StepState>>;
  };
  last_error: string | null;
  has_credentials: boolean;
  running: boolean;
  created_at?: string;
  updated_at?: string;
}

interface UploadedFile {
  name: string;
  size: number;
  uploadedAt: string | null;
  table: string | null;
}

interface StepPlan {
  name: StepName;
  blockedBy: string | null;
  required: boolean;
  dryRun: boolean;
  /** The platform's own description of the step. */
  detail?: string;
}

/** The site wizard's build of the site the store moves into. */
interface SiteBuild {
  jobId: string;
  status: string;
  error: string | null;
  progress: { step: string; status: string; message?: string }[];
}

const BASE = '/admin/platform/store-migrations';

const STEP_LABELS: Record<string, { title: string; detail: string }> = {
  catalog: { title: 'Catalogue', detail: 'Categories, products, options, stock and the menu.' },
  images: { title: 'Product photos', detail: "Copied from the old store into the site's storage." },
  pages: { title: 'Content pages', detail: "The store's articles: about, policies, help." },
  blog: { title: 'Blog posts', detail: "The store's blog, with its dates, bylines and pictures." },
  people: { title: 'Customers and orders', detail: 'Accounts without logins, addresses, every order and its lines. No mail is sent.' },
  'newer-orders': {
    title: 'Orders since the export',
    detail: 'Orders placed at the old store after the export files were made, read through its API link. Run again to catch up.',
  },
  extras: { title: 'Coupons, gift cards, tracking', detail: 'Live coupon codes, gift cards with a balance, tracking numbers.' },
  reviews: { title: 'Reviews', detail: "The store's product reviews, and/or a reviews app's export (.csv)." },
  links: { title: 'Links', detail: 'Links inside imported text pointed at the new site.' },
  brand: {
    title: 'Brand and front page',
    detail: "The logo, phone, social links, footer and the home page's sections, as checked above.",
  },
  files: { title: 'Linked files', detail: 'Pictures, sound clips and documents that text still loads from the old store.' },
  reconcile: { title: 'Check', detail: 'Read-only: what the files hold against what is on the site.' },
};

/** A step's name on the screen; a step the screen does not know yet shows as named. */
const stepTitle = (name: string) => STEP_LABELS[name]?.title ?? name;

/** Credential fields with nothing typed left out; null when nothing was typed. */
function typedCredentials(values: Record<string, string>): Record<string, string> | null {
  const filled = Object.entries(values)
    .map(([k, v]) => [k, v.trim()] as const)
    .filter(([, v]) => v);
  return filled.length ? Object.fromEntries(filled) : null;
}

/** A connector's credential fields as inputs. */
function CredentialInputs({
  connector,
  values,
  onChange,
  kept,
}: {
  connector: ConnectorInfo;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** Credentials are stored already: the fields replace them. */
  kept?: boolean;
}) {
  if (!connector.credentials) return null;
  return (
    <>
      {connector.credentials.fields.map((field) => (
        <Input
          key={field.key}
          label={kept ? `${field.label.replace(/ \(optional.*\)$/, '')} (kept; paste to replace)` : field.label}
          type={field.secret ? 'password' : 'text'}
          value={values[field.key] ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={field.placeholder}
        />
      ))}
    </>
  );
}

const formatBytes = (n: number) =>
  n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} GB` : n >= 1024 ** 2 ? `${(n / 1024 ** 2).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

function statusBadge(status: string, running: boolean) {
  if (running) return <Badge variant="blue">Running</Badge>;
  if (status === 'completed') return <Badge variant="green">Completed</Badge>;
  if (status === 'failed') return <Badge variant="red">Failed</Badge>;
  if (status === 'loading') return <Badge variant="yellow">Stopped</Badge>;
  return <Badge variant="gray">{status}</Badge>;
}

function stepBadge(state?: StepState, running?: boolean) {
  if (!state) return <Badge variant="gray">Not run</Badge>;
  if (state.status === 'running') return running ? <Badge variant="blue">Running</Badge> : <Badge variant="yellow">Interrupted</Badge>;
  if (state.status === 'done') return <Badge variant="green">Done</Badge>;
  if (state.status === 'partial') return <Badge variant="yellow">Part done</Badge>;
  if (state.status === 'skipped') return <Badge variant="gray">Left out</Badge>;
  return <Badge variant="red">Failed</Badge>;
}

/** Sends one file in the pieces the server asks for, reporting the bytes sent so far. */
async function uploadFile(migrationId: string, file: File, onProgress: (sent: number) => void): Promise<void> {
  const start = await api.post<{ uploadId: string; partBytes: number }>(`${BASE}/${migrationId}/uploads`, { name: file.name });
  const name = encodeURIComponent(file.name);
  const parts: { partNumber: number; etag: string }[] = [];
  try {
    for (let offset = 0, partNumber = 1; offset < file.size; offset += start.partBytes, partNumber++) {
      const piece = file.slice(offset, offset + start.partBytes);
      const { etag } = await api.putBytes<{ etag: string }>(
        `${BASE}/${migrationId}/uploads/${name}/parts/${partNumber}?uploadId=${encodeURIComponent(start.uploadId)}`,
        piece,
      );
      parts.push({ partNumber, etag });
      onProgress(Math.min(file.size, offset + piece.size));
    }
    await api.post(`${BASE}/${migrationId}/uploads/${name}/complete`, { uploadId: start.uploadId, parts });
  } catch (err) {
    await api.delete(`${BASE}/${migrationId}/uploads/${name}?uploadId=${encodeURIComponent(start.uploadId)}`).catch(() => {});
    throw err;
  }
}

function NotOnPlatform() {
  return (
    <div className="p-6">
      <Card padding="lg">
        <h1 className="text-xl font-semibold text-ink mb-2">Store migrations</h1>
        <p className="text-ink-muted">Store migrations run from the platform's own admin at rumio.world.</p>
      </Card>
    </div>
  );
}

function MigrationList() {
  const addToast = useUIStore((s) => s.addToast);
  const navigate = useNavigate();
  const [migrations, setMigrations] = useState<Migration[] | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ platform: '', sourceUrl: '', targetSiteId: '', timeZone: 'America/New_York' });
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [connectors, setConnectors] = useState<ConnectorInfo[]>([]);
  const [saving, setSaving] = useState(false);
  const connector = connectors.find((c) => c.platform === form.platform) ?? null;

  useEffect(() => {
    api
      .get<{ connectors?: ConnectorInfo[]; defaultPlatform?: string }>(`${BASE}/connectors`)
      .then((res) => {
        const list = Array.isArray(res?.connectors) ? res.connectors : [];
        setConnectors(list);
        setForm((f) => ({ ...f, platform: f.platform || res.defaultPlatform || list[0]?.platform || '' }));
      })
      .catch(() => setConnectors([]));
  }, []);

  useEffect(() => {
    api
      .get<{ migrations: Migration[] }>(BASE)
      .then((res) => setMigrations(res.migrations))
      .catch((err) => {
        addToast(err?.message || 'Failed to load migrations', 'error');
        setMigrations([]);
      });
  }, [addToast]);

  async function create() {
    try {
      setSaving(true);
      const body: Record<string, unknown> = { sourceUrl: form.sourceUrl.trim(), timeZone: form.timeZone.trim() };
      if (form.platform) body.platform = form.platform;
      const typed = typedCredentials(credentials);
      if (typed) body.credentials = typed;
      if (form.targetSiteId.trim()) body.targetSiteId = form.targetSiteId.trim();
      const res = await api.post<{ migration: Migration }>(BASE, body);
      navigate(`/admin/platform/store-migrations/${res.migration.id}`);
    } catch (err: any) {
      addToast(err?.message || 'Failed to create the migration', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (!migrations) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-ink">Store Migrations</h1>
          <p className="text-ink-muted mt-1">
            Move a store from another platform into a Rumio site: upload the store's export files (and connect its API
            where the platform has one), then run the import. Customers and orders come over silently — nothing is
            emailed.
          </p>
        </div>
        <Button variant="primary" size="md" onClick={() => setOpen(true)}>
          <Plus className="w-5 h-5 mr-2" />
          New Migration
        </Button>
      </div>

      {migrations.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <Truck className="w-16 h-16 mx-auto mb-4 text-ink-soft" />
            <h3 className="text-lg font-semibold text-ink mb-2">No migrations yet</h3>
            <p className="text-ink-muted">Start one with the store's address and the site it moves into.</p>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left text-ink-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Store</th>
                <th className="px-4 py-3 font-medium">Into site</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {migrations.map((m) => (
                <tr key={m.id} className="hover:bg-surface-muted">
                  <td className="px-4 py-3">
                    <Link to={`/admin/platform/store-migrations/${m.id}`} className="font-medium text-ink hover:underline">
                      {m.source_url.replace(/^https?:\/\//, '')}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{m.target_site_id ?? '—'}</td>
                  <td className="px-4 py-3">{statusBadge(m.status, m.running)}</td>
                  <td className="px-4 py-3 text-ink-soft">{when(m.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal isOpen={open} onClose={() => setOpen(false)} title="New Store Migration">
        <div className="space-y-4">
          {connectors.length > 0 && (
            <Select
              label="Moving from"
              value={form.platform}
              onChange={(e) => {
                setForm((f) => ({ ...f, platform: e.target.value }));
                setCredentials({});
              }}
              options={connectors.map((c) => ({ value: c.platform, label: c.label }))}
              hint={connector?.uploads.hint}
            />
          )}
          <Input
            label="The store's address"
            value={form.sourceUrl}
            onChange={(e) => setForm((f) => ({ ...f, sourceUrl: e.target.value }))}
            placeholder="https://www.example.com"
          />
          <Input
            label="Site it moves into (site id, optional for now)"
            value={form.targetSiteId}
            onChange={(e) => setForm((f) => ({ ...f, targetSiteId: e.target.value }))}
            placeholder="example"
          />
          <Input
            label={
              connector?.credentials && !connector.credentials.optional
                ? 'Store time zone'
                : 'Store time zone (dates in the export files are its local time)'
            }
            value={form.timeZone}
            onChange={(e) => setForm((f) => ({ ...f, timeZone: e.target.value }))}
          />
          {connector && (
            <CredentialInputs
              connector={connector}
              values={credentials}
              onChange={(key, value) => setCredentials((c) => ({ ...c, [key]: value }))}
            />
          )}
          {connector?.credentials?.note && <p className="text-xs text-ink-muted">{connector.credentials.note}</p>}
          <div className="flex justify-end gap-3 pt-2 border-t border-line">
            <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={create} disabled={saving || !form.sourceUrl.trim()}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Create
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** A step's report as short lines: top-level counts, nested objects flattened one level. */
function ReportLines({ report }: { report: Record<string, unknown> }) {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(report)) {
    if (key === 'lines' || key === 'note') continue;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const inner = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean')
        .map(([k, v]) => `${k} ${v}`);
      if (inner.length) lines.push(`${key}: ${inner.join(', ')}`);
    } else if (!Array.isArray(value)) {
      lines.push(`${key}: ${String(value)}`);
    }
  }
  return (
    <ul className="text-xs text-ink-soft space-y-0.5 font-mono">
      {lines.map((l) => (
        <li key={l} className="break-words">
          {l}
        </li>
      ))}
    </ul>
  );
}

interface GiftCardToReissue {
  lastCharacters: string;
  balance: number;
  currency: string | null;
  expiresOn: string | null;
  customerEmail: string | null;
}

/** The gift cards a platform won't give full codes for, found at the top of a report or one level down. */
function reissueList(report: Record<string, unknown>): GiftCardToReissue[] | null {
  const pick = (r: Record<string, unknown>) => (Array.isArray(r.giftCardsToReissue) ? (r.giftCardsToReissue as GiftCardToReissue[]) : null);
  const top = pick(report);
  if (top) return top;
  for (const value of Object.values(report)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const inner = pick(value as Record<string, unknown>);
      if (inner) return inner;
    }
  }
  return null;
}

function csvCell(value: string | number | null): string {
  const text = value === null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Gift cards to re-issue by hand, with a CSV the merchant can work from. */
function GiftCardReissueTable({ cards }: { cards: GiftCardToReissue[] }) {
  if (cards.length === 0) return null;
  const download = () => {
    const rows = [['Last characters', 'Balance', 'Currency', 'Expires', 'Customer email'], ...cards.map((c) => [c.lastCharacters, c.balance, c.currency, c.expiresOn, c.customerEmail])];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gift-cards-to-reissue.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-ink-muted">
          {cards.length} gift {cards.length === 1 ? 'card' : 'cards'} to re-issue: the old platform does not give out full codes, so none were created.
        </p>
        <Button size="sm" variant="secondary" onClick={download}>
          Download CSV
        </Button>
      </div>
      <table className="w-full text-xs mt-2">
        <thead className="text-left text-ink-muted">
          <tr>
            <th className="py-1 pr-3 font-medium">Ends in</th>
            <th className="py-1 pr-3 font-medium text-right">Balance</th>
            <th className="py-1 pr-3 font-medium">Customer</th>
            <th className="py-1 font-medium">Expires</th>
          </tr>
        </thead>
        <tbody>
          {cards.slice(0, 50).map((c, i) => (
            <tr key={i} className="border-t border-line">
              <td className="py-1 pr-3 text-ink font-mono">{c.lastCharacters}</td>
              <td className="py-1 pr-3 text-right text-ink-soft">
                {c.balance.toFixed(2)} {c.currency ?? ''}
              </td>
              <td className="py-1 pr-3 text-ink-soft break-all">{c.customerEmail ?? '-'}</td>
              <td className="py-1 text-ink-soft">{c.expiresOn ?? 'never'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {cards.length > 50 && <p className="text-xs text-ink-muted mt-1">and {cards.length - 50} more in the CSV.</p>}
    </div>
  );
}

interface ReconcileLine {
  kind: string;
  inFiles: number;
  leftOut: number;
  expected: number;
  onSite: number;
  missing: number;
}

function ReconcileTable({ lines }: { lines: ReconcileLine[] }) {
  return (
    <table className="w-full text-xs mt-2">
      <thead className="text-left text-ink-muted">
        <tr>
          <th className="py-1 pr-3 font-medium">Kind</th>
          <th className="py-1 pr-3 font-medium text-right">In files</th>
          <th className="py-1 pr-3 font-medium text-right">Left out</th>
          <th className="py-1 pr-3 font-medium text-right">Expected</th>
          <th className="py-1 pr-3 font-medium text-right">On site</th>
          <th className="py-1 font-medium text-right">Missing</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.kind} className="border-t border-line">
            <td className="py-1 pr-3 text-ink">{l.kind}</td>
            <td className="py-1 pr-3 text-right text-ink-soft">{l.inFiles.toLocaleString()}</td>
            <td className="py-1 pr-3 text-right text-ink-soft">{l.leftOut.toLocaleString()}</td>
            <td className="py-1 pr-3 text-right text-ink-soft">{l.expected.toLocaleString()}</td>
            <td className="py-1 pr-3 text-right text-ink-soft">{l.onSite.toLocaleString()}</td>
            <td className={`py-1 text-right font-semibold ${l.missing ? 'text-red-600 dark:text-red-400' : 'text-ink-soft'}`}>{l.missing.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A site id from the store's address: www.example-shop.com → example-shop. */

/**
 * Builds the site the store moves into with the site wizard: nothing
 * generated, nothing mailed, payments off, maintenance mode on.
 */
function SiteCard({
  migration,
  build,
  suggestedSiteId,
  onChange,
}: {
  migration: Migration;
  build: SiteBuild | null;
  /** A free id the server offers; empty when the store's own name is taken. */
  suggestedSiteId?: string | null;
  onChange: () => Promise<unknown>;
}) {
  const addToast = useUIStore((s) => s.addToast);
  const [form, setForm] = useState({
    siteId: suggestedSiteId ?? '',
    storeName: '',
    tagline: '',
    productTypeDescription: '',
    primaryColor: '',
    accentColor: '',
    fontSans: '',
    fontDisplay: '',
  });
  const [busy, setBusy] = useState(false);

  // The look read from the store fills in what is still empty, once per read or save.
  const look = migration.options.look;
  const lookVersion = `${migration.options.brandReadAt ?? ''}|${migration.options.brandEditedAt ?? ''}`;
  useEffect(() => {
    if (!look) return;
    setForm((f) => ({
      ...f,
      storeName: f.storeName || look.storeName || '',
      tagline: f.tagline || look.tagline || '',
      primaryColor: f.primaryColor || look.primaryColor || '',
      accentColor: f.accentColor || look.accentColor || '',
      fontSans: f.fontSans || look.fontSans || '',
      fontDisplay: f.fontDisplay || look.fontDisplay || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookVersion]);

  async function create() {
    try {
      setBusy(true);
      const body: Record<string, string> = {};
      for (const [k, v] of Object.entries(form)) if (v.trim()) body[k] = v.trim();
      await api.post(`${BASE}/${migration.id}/site`, body);
      addToast(`Building ${form.siteId}.rumio.world`, 'success');
      await onChange();
    } catch (err: any) {
      addToast(err?.message || 'Failed to start the site', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function retry() {
    try {
      setBusy(true);
      await api.post(`${BASE}/${migration.id}/site/retry`, {});
      await onChange();
    } catch (err: any) {
      addToast(err?.message || 'Failed to retry', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (build) {
    const done = build.status === 'completed';
    return (
      <Card padding="lg">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-ink">Site {migration.target_site_id}</h2>
          {done ? (
            <Badge variant="green">Built</Badge>
          ) : build.status === 'failed' ? (
            <Badge variant="red">Build failed</Badge>
          ) : (
            <Badge variant="blue">Building</Badge>
          )}
        </div>
        {done ? (
          <p className="text-sm text-ink-muted">
            Up at {migration.target_site_id}.rumio.world, in maintenance mode. Run the steps below to bring the store over.
          </p>
        ) : (
          <ul className="text-xs space-y-0.5 font-mono">
            {build.progress.map((p) => (
              <li key={p.step} className={p.status === 'failed' ? 'text-red-600 dark:text-red-400' : 'text-ink-soft'}>
                {p.status} · {p.step}
                {p.message ? ` — ${p.message}` : ''}
              </li>
            ))}
          </ul>
        )}
        {build.error && <p className="mt-2 text-sm text-red-600 dark:text-red-400 break-words">{build.error}</p>}
        {build.status === 'failed' && (
          <div className="flex justify-end mt-3">
            <Button variant="secondary" size="sm" onClick={retry} disabled={busy}>
              Try again
            </Button>
          </div>
        )}
      </Card>
    );
  }

  const field = (key: keyof typeof form, label: string, placeholder = '') => (
    <Input
      label={label}
      value={form[key]}
      placeholder={placeholder}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
    />
  );
  return (
    <Card padding="lg">
      <h2 className="text-lg font-semibold text-ink mb-1">Create the site</h2>
      <p className="text-sm text-ink-muted mb-4">
        A new site at <span className="font-mono">{form.siteId || '…'}.rumio.world</span>, in maintenance mode, with no
        generated content, no payments and no welcome email. Or name an existing site in Settings instead.
        {!look && ' Read the brand from the store first to fill in its name, colours and fonts.'}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {field('siteId', 'Site id')}
        {field('storeName', "Store's name")}
        {field('tagline', 'Tagline (optional)')}
        {field('productTypeDescription', 'What it sells (one sentence)')}
        {field('primaryColor', 'Main colour', '#d2232a')}
        {field('accentColor', 'Second colour (optional)', '#860000')}
        {field('fontSans', 'Body font (Google Fonts name, optional)', 'Lexend')}
        {field('fontDisplay', 'Heading font (optional)', 'Yeseva One')}
      </div>
      <div className="flex justify-end mt-4">
        <Button
          variant="primary"
          size="sm"
          onClick={create}
          disabled={
            busy || !form.siteId.trim() || !form.storeName.trim() || !form.productTypeDescription.trim() || !form.primaryColor.trim()
          }
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          Create site
        </Button>
      </div>
    </Card>
  );
}

function MigrationDetail({ id }: { id: string }) {
  const addToast = useUIStore((s) => s.addToast);
  const [migration, setMigration] = useState<Migration | null>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [plan, setPlan] = useState<StepPlan[]>([]);
  const [build, setBuild] = useState<SiteBuild | null>(null);
  const [suggestedSiteId, setSuggestedSiteId] = useState<string | null>(null);
  const [targetSiteMissing, setTargetSiteMissing] = useState(false);
  const [log, setLog] = useState('');
  const [uploading, setUploading] = useState<{ name: string; sent: number; size: number } | null>(null);
  const [connector, setConnector] = useState<ConnectorInfo | null>(null);
  const [settings, setSettings] = useState({ targetSiteId: '', timeZone: '' });
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const res = await api.get<{
      migration: Migration;
      connector?: ConnectorInfo | null;
      files: UploadedFile[];
      steps: StepPlan[];
      site: SiteBuild | null;
      suggestedSiteId?: string | null;
      targetSiteMissing?: boolean;
    }>(`${BASE}/${id}`);
    setMigration(res.migration);
    setConnector(res.connector ?? null);
    setFiles(res.files);
    setPlan(res.steps);
    setBuild(res.site ?? null);
    setSuggestedSiteId(res.suggestedSiteId ?? null);
    setTargetSiteMissing(!!res.targetSiteMissing);
    const logRes = await api.get<{ log: string }>(`${BASE}/${id}/log`);
    setLog(logRes.log);
    return res.migration;
  }, [id]);

  useEffect(() => {
    load()
      .then((m) => setSettings({ targetSiteId: m.target_site_id ?? '', timeZone: m.options.timeZone ?? 'America/New_York' }))
      .catch((err) => addToast(err?.message || 'Failed to load the migration', 'error'));
  }, [load, addToast]);

  // Follow a run, or the site's build, while it lasts.
  const building = !!build && build.status !== 'completed' && build.status !== 'failed';
  useEffect(() => {
    if (!migration?.running && !building) return;
    const timer = setInterval(() => {
      load().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [migration?.running, building, load]);

  async function saveSettings() {
    try {
      setBusy(true);
      const body: Record<string, unknown> = { targetSiteId: settings.targetSiteId.trim() || null };
      if (settings.timeZone.trim()) body.timeZone = settings.timeZone.trim();
      const typed = typedCredentials(credentials);
      if (typed) body.credentials = typed;
      await api.patch(`${BASE}/${id}`, body);
      setCredentials({});
      addToast('Saved', 'success');
      await load();
    } catch (err: any) {
      addToast(err?.message || 'Failed to save', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function addFiles(list: FileList | null) {
    if (!list?.length) return;
    for (const file of Array.from(list)) {
      setUploading({ name: file.name, sent: 0, size: file.size });
      try {
        await uploadFile(id, file, (sent) => setUploading({ name: file.name, sent, size: file.size }));
      } catch (err: any) {
        addToast(`${file.name}: ${err?.message || 'upload failed'}`, 'error');
        break;
      }
    }
    setUploading(null);
    if (input.current) input.current.value = '';
    await load().catch(() => {});
  }

  async function removeFile(name: string) {
    try {
      await api.delete(`${BASE}/${id}/uploads/${encodeURIComponent(name)}`);
      await load();
    } catch (err: any) {
      addToast(err?.message || 'Failed to remove the file', 'error');
    }
  }

  async function run(steps?: StepName[], dryRun = false) {
    try {
      setBusy(true);
      await api.post(`${BASE}/${id}/run`, { ...(steps ? { steps } : {}), ...(dryRun ? { dryRun: true } : {}) });
      const what = steps ? steps.map(stepTitle).join(', ') : 'the migration';
      addToast(`Started${dryRun ? ' a dry run of' : ''}: ${what}`, 'success');
      await load();
    } catch (err: any) {
      addToast(err?.message || 'Failed to start', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!migration) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  const states = migration.progress.steps ?? {};
  // Nothing loads into a site that is still being built.
  const locked = migration.running || busy || !!uploading || (!!build && build.status !== 'completed');

  return (
    <div className="p-6 space-y-6">
      <div>
        <Link to="/admin/platform/store-migrations" className="inline-flex items-center text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="w-4 h-4 mr-1" /> All migrations
        </Link>
        <div className="flex items-center justify-between gap-4 mt-2">
          <div>
            <h1 className="text-2xl font-bold text-ink">
              {migration.source_url.replace(/^https?:\/\//, '')} → {migration.target_site_id ?? 'no site chosen'}
            </h1>
            <p className="text-ink-muted mt-1 text-sm">
              {migration.running
                ? `Running since ${when(migration.progress.run?.startedAt)}.`
                : migration.progress.run?.finishedAt
                  ? `Last run finished ${when(migration.progress.run.finishedAt)}.`
                  : migration.progress.run
                    ? 'The last run stopped without finishing (the server restarted). Run it again; finished work is kept.'
                    : 'Not run yet.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {statusBadge(migration.status, migration.running)}
            <Button variant="secondary" size="sm" onClick={() => load().catch(() => {})}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {migration.last_error && !migration.running && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400 break-words">{migration.last_error}</p>
        )}
      </div>

      <StoreMigrationBrand
        migrationId={migration.id}
        sourceUrl={migration.source_url}
        options={migration.options}
        locked={migration.running || busy}
        onChange={load}
      />

      {(build || !migration.target_site_id || targetSiteMissing) && (
        <SiteCard
          migration={migration}
          build={build}
          suggestedSiteId={targetSiteMissing ? migration.target_site_id : suggestedSiteId}
          onChange={load}
        />
      )}

      <Card padding="lg">
        <h2 className="text-lg font-semibold text-ink mb-4">Settings</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <Input
            label="Site it moves into (site id)"
            value={settings.targetSiteId}
            onChange={(e) => setSettings((s) => ({ ...s, targetSiteId: e.target.value }))}
            placeholder="example"
          />
          <Input
            label="Store time zone"
            value={settings.timeZone}
            onChange={(e) => setSettings((s) => ({ ...s, timeZone: e.target.value }))}
          />
          {connector && (
            <CredentialInputs
              connector={connector}
              values={credentials}
              onChange={(key, value) => setCredentials((c) => ({ ...c, [key]: value }))}
              kept={migration.has_credentials}
            />
          )}
        </div>
        <p className="text-xs text-ink-muted mt-3">
          The run writes into that site's database directly. Point it only at a site made for this store.
        </p>
        <div className="flex justify-end mt-4">
          <Button variant="primary" size="sm" onClick={saveSettings} disabled={locked}>
            Save
          </Button>
        </div>
      </Card>

      <Card padding="lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-ink">Export files</h2>
            <p className="text-sm text-ink-muted">
              {connector ? `${connector.label}: ${connector.uploads.hint}` : "The store's export files."} File names as
              downloaded.
            </p>
          </div>
          <div>
            <input
              ref={input}
              type="file"
              multiple
              accept={(connector?.uploads.accept ?? ['.xml', '.csv']).join(',')}
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <Button variant="secondary" size="sm" onClick={() => input.current?.click()} disabled={locked}>
              <FileUp className="w-4 h-4 mr-2" />
              Add files
            </Button>
          </div>
        </div>
        {uploading && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-ink-soft mb-1">
              <span>{uploading.name}</span>
              <span>
                {formatBytes(uploading.sent)} of {formatBytes(uploading.size)}
              </span>
            </div>
            <div className="h-2 rounded bg-surface-muted overflow-hidden">
              <div className="h-2 bg-sage-600" style={{ width: `${uploading.size ? (uploading.sent / uploading.size) * 100 : 0}%` }} />
            </div>
          </div>
        )}
        {files.length === 0 ? (
          <p className="text-sm text-ink-muted">No files yet.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-line">
              {files.map((f) => (
                <tr key={f.name}>
                  <td className="py-2 pr-3 font-mono text-ink break-all">{f.name}</td>
                  <td className="py-2 pr-3 text-ink-soft">{f.table === 'reviews' ? 'Reviews export (CSV)' : f.table ?? <span className="text-red-600 dark:text-red-400">not an export name</span>}</td>
                  <td className="py-2 pr-3 text-ink-soft text-right whitespace-nowrap">{formatBytes(f.size)}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => removeFile(f.name)}
                      disabled={locked}
                      className="text-ink-muted hover:text-red-600 dark:hover:text-red-400 disabled:opacity-40"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card padding="lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-ink">Steps</h2>
          <Button variant="primary" size="sm" onClick={() => run()} disabled={locked || !migration.target_site_id}>
            <Play className="w-4 h-4 mr-2" />
            Run all
          </Button>
        </div>
        <ul className="divide-y divide-line">
          {plan.map((p) => {
            const state = states[p.name];
            return (
              <li key={p.name} className="py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink">{stepTitle(p.name)}</span>
                      {stepBadge(state, migration.running)}
                    </div>
                    <p className="text-sm text-ink-muted">{p.detail ?? STEP_LABELS[p.name]?.detail}</p>
                    {p.blockedBy && <p className="text-xs text-ink-muted mt-1">Cannot run yet: {p.blockedBy}.</p>}
                    {state?.reason && <p className="text-xs text-ink-muted mt-1">Left out: {state.reason}.</p>}
                    {state?.error && <p className="text-xs text-red-600 dark:text-red-400 mt-1 break-words">{state.error}</p>}
                    {state?.report && (
                      <div className="mt-2">
                        {Array.isArray(state.report.lines) ? (
                          <ReconcileTable lines={state.report.lines as ReconcileLine[]} />
                        ) : (
                          <>
                            <ReportLines report={state.report} />
                            {reissueList(state.report) && <GiftCardReissueTable cards={reissueList(state.report)!} />}
                          </>
                        )}
                      </div>
                    )}
                    {state?.warnings && (
                      <details className="mt-1 text-xs text-ink-muted">
                        <summary className="cursor-pointer">{state.warnings.count} warnings</summary>
                        <ul className="mt-1 space-y-0.5">
                          {state.warnings.sample.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {state?.finishedAt && <p className="text-xs text-ink-muted mt-1">Finished {when(state.finishedAt)}</p>}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {p.dryRun && p.name !== 'reconcile' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => run([p.name], true)}
                        disabled={locked || !migration.target_site_id || !!p.blockedBy}
                        title="Read and count only; nothing is written"
                      >
                        Dry run
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => run([p.name])}
                      disabled={locked || !migration.target_site_id || !!p.blockedBy}
                    >
                      Run
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card padding="lg">
        <h2 className="text-lg font-semibold text-ink mb-2">Run log</h2>
        {log ? (
          <pre className="text-xs text-ink-soft bg-surface-muted rounded p-3 max-h-80 overflow-auto whitespace-pre-wrap">{log}</pre>
        ) : (
          <p className="text-sm text-ink-muted">Nothing logged yet (the log is kept on the server that ran it).</p>
        )}
      </Card>
    </div>
  );
}

const StoreMigrations = () => {
  const { id } = useParams<{ id?: string }>();
  if (SITE_ID !== 'rumio') return <NotOnPlatform />;
  return id ? <MigrationDetail id={id} /> : <MigrationList />;
};

export default StoreMigrations;
